/**
 * Admin - Billing Management
 * Gestão de faturamento e pagamentos
 */
import { useState, useEffect } from 'react'
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'
import {
  CreditCard,
  DollarSign,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Download,
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  Calendar,
  Building2,
  Receipt,
  Eye,
} from 'lucide-react'
import { adminDb } from '../lib/supabase'

interface Payment {
  id: string
  company_id: string
  company_name: string
  amount: number
  status: 'paid' | 'pending' | 'failed' | 'refunded'
  method: 'credit_card' | 'pix' | 'boleto'
  invoice_id: string
  created_at: string
  paid_at?: string
}

interface BillingStats {
  mrr: number
  arr: number
  totalRevenue: number
  pendingAmount: number
  averageTicket: number
  churnRate: number
}

const COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444']

export default function Billing() {
  const [loading, setLoading] = useState(true)
  const [payments, setPayments] = useState<Payment[]>([])
  const [stats, setStats] = useState<BillingStats>({
    mrr: 89450,
    arr: 1073400,
    totalRevenue: 534200,
    pendingAmount: 12450,
    averageTicket: 850,
    churnRate: 2.3,
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [methodFilter, setMethodFilter] = useState<string>('all')
  const [revenueData, setRevenueData] = useState<{ month: string; revenue: number; target: number }[]>([])
  const [methodDistribution, setMethodDistribution] = useState<{ name: string; value: number }[]>([])

  useEffect(() => {
    loadBillingData()
  }, [])

  const loadBillingData = async () => {
    try {
      setLoading(true)

      // Load payments
      const { data: paymentsData } = await adminDb.payments.getRecent(50)

      if (paymentsData && paymentsData.length > 0) {
        setPayments(paymentsData.map((p: any) => ({
          id: p.id,
          company_id: p.company_id,
          company_name: p.companies?.name || 'Empresa',
          amount: p.amount || 0,
          status: p.status || 'pending',
          method: p.method || 'credit_card',
          invoice_id: p.invoice_id || `INV-${p.id?.slice(0, 8)}`,
          created_at: p.created_at,
          paid_at: p.paid_at,
        })))
      } else {
        // Demo data
        setPayments([
          { id: '1', company_id: '1', company_name: 'ContruBem S.A.', amount: 4500, status: 'paid', method: 'credit_card', invoice_id: 'INV-001234', created_at: '2024-11-28', paid_at: '2024-11-28' },
          { id: '2', company_id: '2', company_name: 'TeleNord Comunicações', amount: 2800, status: 'paid', method: 'pix', invoice_id: 'INV-001235', created_at: '2024-11-27', paid_at: '2024-11-27' },
          { id: '3', company_id: '3', company_name: 'Hospital Santa Cruz', amount: 5200, status: 'pending', method: 'boleto', invoice_id: 'INV-001236', created_at: '2024-11-26' },
          { id: '4', company_id: '4', company_name: 'Indústria MetalMax', amount: 1800, status: 'failed', method: 'credit_card', invoice_id: 'INV-001237', created_at: '2024-11-25' },
          { id: '5', company_id: '5', company_name: 'Energia Solar Plus', amount: 950, status: 'paid', method: 'pix', invoice_id: 'INV-001238', created_at: '2024-11-24', paid_at: '2024-11-24' },
          { id: '6', company_id: '6', company_name: 'LogiTrans Transportes', amount: 1500, status: 'refunded', method: 'credit_card', invoice_id: 'INV-001239', created_at: '2024-11-23' },
        ])
      }

      // Generate revenue chart data
      const months = ['Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov']
      setRevenueData(months.map((month, i) => ({
        month,
        revenue: 65000 + (i * 8000) + Math.random() * 5000,
        target: 70000 + (i * 7000),
      })))

      // Method distribution
      setMethodDistribution([
        { name: 'Cartão de Crédito', value: 55 },
        { name: 'PIX', value: 30 },
        { name: 'Boleto', value: 15 },
      ])

    } catch (error) {
      console.error('Erro ao carregar dados de billing:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleExport = () => {
    let csv = 'ID,Empresa,Valor,Status,Método,Data\n'
    filteredPayments.forEach(p => {
      csv += `"${p.invoice_id}","${p.company_name}",${p.amount},"${p.status}","${p.method}","${p.created_at}"\n`
    })

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `pagamentos_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string; icon: any }> = {
      paid: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', icon: CheckCircle2 },
      pending: { bg: 'bg-amber-500/10', text: 'text-amber-400', icon: Clock },
      failed: { bg: 'bg-rose-500/10', text: 'text-rose-400', icon: XCircle },
      refunded: { bg: 'bg-slate-500/10', text: 'text-slate-400', icon: RefreshCw },
    }
    const style = styles[status] || styles.pending
    const Icon = style.icon
    const labels: Record<string, string> = {
      paid: 'Pago',
      pending: 'Pendente',
      failed: 'Falhou',
      refunded: 'Reembolsado',
    }

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="w-3 h-3" />
        {labels[status] || status}
      </span>
    )
  }

  const getMethodBadge = (method: string) => {
    const labels: Record<string, string> = {
      credit_card: '💳 Cartão',
      pix: '⚡ PIX',
      boleto: '📄 Boleto',
    }

    return (
      <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-700/50 text-slate-300">
        {labels[method] || method}
      </span>
    )
  }

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.invoice_id.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter
    const matchesMethod = methodFilter === 'all' || payment.method === methodFilter

    return matchesSearch && matchesStatus && matchesMethod
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 text-violet-400 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-violet-400" />
            Faturamento
          </h1>
          <p className="text-slate-400">Gestão de pagamentos e receita</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Exportar
          </button>
          <button
            onClick={loadBillingData}
            className="px-4 py-2 bg-violet-600 hover:bg-violet-500 rounded-lg text-white transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-slate-500">MRR</span>
          </div>
          <p className="text-xl font-bold text-white">R$ {stats.mrr.toLocaleString('pt-BR')}</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-slate-500">ARR</span>
          </div>
          <p className="text-xl font-bold text-white">R$ {(stats.arr / 1000).toFixed(0)}k</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-violet-400" />
            <span className="text-xs text-slate-500">Receita Total</span>
          </div>
          <p className="text-xl font-bold text-white">R$ {(stats.totalRevenue / 1000).toFixed(0)}k</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-slate-500">Pendente</span>
          </div>
          <p className="text-xl font-bold text-amber-400">R$ {stats.pendingAmount.toLocaleString('pt-BR')}</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <div className="flex items-center gap-2 mb-2">
            <Receipt className="w-4 h-4 text-cyan-400" />
            <span className="text-xs text-slate-500">Ticket Médio</span>
          </div>
          <p className="text-xl font-bold text-white">R$ {stats.averageTicket.toLocaleString('pt-BR')}</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-4 h-4 text-rose-400" />
            <span className="text-xs text-slate-500">Churn</span>
          </div>
          <p className="text-xl font-bold text-white">{stats.churnRate}%</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 admin-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              Receita Mensal
            </h2>
            <span className="text-xs text-slate-500">Últimos 6 meses</span>
          </div>

          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
              <XAxis dataKey="month" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}
                formatter={(value: number) => [`R$ ${value.toLocaleString('pt-BR')}`, '']}
              />
              <Bar dataKey="revenue" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Receita" />
              <Bar dataKey="target" fill="#374151" radius={[4, 4, 0, 0]} name="Meta" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Payment Methods */}
        <div className="admin-card">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-cyan-400" />
            Métodos de Pagamento
          </h2>

          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={methodDistribution}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {methodDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="space-y-2 mt-4">
            {methodDistribution.map((item, index) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }} />
                  <span className="text-sm text-slate-300">{item.name}</span>
                </div>
                <span className="text-sm font-medium text-white">{item.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
          <input
            type="text"
            placeholder="Buscar por empresa ou fatura..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
          />
        </div>

        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
          >
            <option value="all">Todos os Status</option>
            <option value="paid">Pago</option>
            <option value="pending">Pendente</option>
            <option value="failed">Falhou</option>
            <option value="refunded">Reembolsado</option>
          </select>

          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            className="px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
          >
            <option value="all">Todos os Métodos</option>
            <option value="credit_card">Cartão de Crédito</option>
            <option value="pix">PIX</option>
            <option value="boleto">Boleto</option>
          </select>
        </div>
      </div>

      {/* Payments Table */}
      <div className="admin-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="text-left px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Fatura
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Valor
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Método
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Data
                </th>
                <th className="text-right px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                    Nenhum pagamento encontrado
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-sm font-mono text-cyan-400">{payment.invoice_id}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-white">{payment.company_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-white">
                        R$ {payment.amount.toLocaleString('pt-BR')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {getMethodBadge(payment.method)}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(payment.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-400">
                          {new Date(payment.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                          title="Ver detalhes"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-slate-700/50 flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Mostrando {filteredPayments.length} de {payments.length} pagamentos
          </p>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
              Anterior
            </button>
            <button className="px-3 py-1.5 text-sm bg-violet-600 text-white rounded-lg">
              1
            </button>
            <button className="px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
              Próximo
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
